import { type PropsWithChildren, useEffect } from 'react';

import IconButton from '@/components/Button/IconButton';
import { VStack } from '@/components/Layout';
import HStack from '@/components/Layout/HStack';
import { Sheet } from '@/components/Layout/Sheet';
import NavigationCard, {
  type NavigationSectionGroup,
} from '@/components/Navigation/NavigationCard';
import { useDesktopOrMobileMediaQuery } from '@/hooks/useMediaQuery';

import { CFormStore, useCFormStore } from './cform-store';
import type { CFormEditUnit, CFormItemLayoutType } from './cform-types';

type Props = PropsWithChildren<{
  /** 編集する単位 */
  editUnit: CFormEditUnit;
  /** 各Itemの見た目（デフォルトでカード表示） */
  itemLayout?: CFormItemLayoutType;
  /** 指定するとナビゲーションを描画 */
  navigationSectionGroups?: NavigationSectionGroup[];
  /** 外側のパディングとナビレイアウトを省略し、children のみをレンダリングする */
  bare?: boolean;
  onSave?: () => Promise<unknown>;
  onReset?: () => void;
}>;

/**
 * Card-based Form / Page
 *
 * DRESS CODE 標準の、台帳系詳細画面のプロフィール(編集モードつき) を簡潔に記述するためのレイアウトです。
 *
 * Navigation と Sections が一体になり、そのままページコンテンツとして活用できます。
 * 以下の形でレイアウトを構成することにより、適切に Navigation を描画します。
 *
 * また、 CForm は cform-store によって状態管理をしており、
 * CForm 内部の CFormSection / CFormItem に対して編集状態等のコントロールを自動的に行います。
 *
 * <CForm>
 *   <CFormSection>
 *     <CFormItem />
 *      ...
 *   </CFormSection>
 *   ...
 * </CForm>
 */
export function CForm(props: Props) {
  const store = CFormStore.createStore();
  return (
    <CFormStore.Provider value={store}>
      <Content {...props} />
    </CFormStore.Provider>
  );
}

function Content(props: Props) {
  const store = useCFormStore();
  useEffect(() => store.actions.init(props), [store.actions, props]);

  const { isMobile } = useDesktopOrMobileMediaQuery();

  if (props.bare) {
    return <VStack className="w-full gap-4">{props.children}</VStack>;
  }

  return (
    <>
      {/* Mobile Navigation */}
      {isMobile && props.navigationSectionGroups && (
        <Sheet
          title="Navigation"
          side="bottom"
          closeOnContentClick
          trigger={
            <IconButton
              icon="OriginalTableOfContentIcon"
              variant="mutedGhost"
              size="Large"
              aria-label="Open navigation"
              className="fixed top-2/7 left-0 rounded-l-none rounded-r-xl bg-neutral-fill/20 shadow-sm backdrop-blur-xs"
            />
          }
        >
          <NavigationCard sections={props.navigationSectionGroups} />
        </Sheet>
      )}

      <HStack className="px-12 py-6 max-md:p-6 gap-8">
        {/* Desktop Navigation */}
        {!isMobile && props.navigationSectionGroups && (
          <nav className="sticky top-8 h-fit w-60">
            <NavigationCard sections={props.navigationSectionGroups} />
          </nav>
        )}

        {/* Contents */}
        <VStack className="w-full gap-4">{props.children}</VStack>
      </HStack>
    </>
  );
}
